/**
 * WorkspaceSitesTab Component
 *
 * Manages the properties (sites) belonging to a workspace.
 *
 * NEW MODEL (1 property → 1 workspace):
 * - Each property belongs to exactly ONE workspace
 * - Properties can be enabled/disabled within the workspace
 * - Moving a property to another workspace removes it from this one
 *
 * Add site always goes through `/add-website?workspace=` (same wizard as the hub).
 * Owner/admin can also move an existing site in from another workspace.
 */

import { useState, useMemo, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { generateSlugId } from '@/lib/routing/slug-id'
import { Button } from '@/components/ui/button'
import {
  SettingsWell,
  SettingsListCard,
  SettingsList,
  SettingsListRow,
  settingsButtonClass,
} from '@/components/shared/settings/SettingsKit'
import { Skeleton } from '@/components/ui/skeleton'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Switch } from '@/components/ui/switch'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { useWorkspace } from '@/lib/contexts/WorkspaceContext'
import { useWorkspaceTeam } from '@/lib/contexts/WorkspaceTeamContext'
import { useAuth } from '@/lib/contexts/AuthContext'
import { useSubscription } from '@/lib/contexts/SubscriptionContext'
import { getAvailableProperties, movePropertyToWorkspace } from '@/lib/api/workspace-team'
import { getAllWorkspaces } from '@/lib/api/workspaces'
import { toast } from 'sonner'
import Favicon from '@/components/shared/Favicon'
import { CreateOrUpgradeButton, buildPlanLimitCopy } from '@/components/shared/plan-usage'
import { getPlanDisplayName } from '@/lib/constants/pricing'
import { EmptyState } from '@/components/shared/layout'
import { cn } from '@/lib/utils'
import { HugeiconsIcon } from '@hugeicons/react'
import {
  EarthIcon,
  Loading01Icon,
} from '@hugeicons/core-free-icons'
import type { AvailableProperty } from '@/lib/types/workspace-team'
import type { WorkspaceWithGrouping } from '@/lib/api/workspaces'

/** Status discipline (DESIGN.md): ink text on a neutral pill, the dot carries the state. */
function TrackingPill({ status }: { status: string }) {
  const state =
    status === 'verified'
      ? { dot: 'bg-live', label: 'Tracking' }
      : status === 'pending_verification'
        ? { dot: 'bg-warning', label: 'Pending' }
        : status === 'error'
          ? { dot: 'bg-danger', label: 'Error' }
          : { dot: 'bg-foreground/30', label: 'Not installed' }
  return (
    <span className="inline-flex h-[22px] items-center gap-1.5 rounded-md bg-canvas px-2 text-xs font-medium leading-4 text-foreground/80">
      <span className={cn('size-1.5 rounded-full', state.dot)} />
      {state.label}
    </span>
  )
}

export default function WorkspaceSitesTab() {
  const {
    workspace,
    properties,
    togglePropertyEnabled,
    isLoading: isWorkspaceLoading,
    refetch,
  } = useWorkspace()
  const { isOwner, isAdmin, members } = useWorkspaceTeam()
  const { token } = useAuth()
  const {
    hasReachedWebsiteLimit,
    websitesUsed,
    websiteLimit,
    isReadOnly,
    readOnlyReason,
    openPricingModal,
    currentPlan,
    planName,
  } = useSubscription()
  const displayPlanName = getPlanDisplayName(currentPlan, planName)
  const siteLimitCopy = buildPlanLimitCopy({
    noun: 'sites',
    limit: websiteLimit,
    planName: displayPlanName,
    resolution: 'Upgrade to add another domain to this workspace.',
  })
  const navigate = useNavigate()

  const [isMoveExistingOpen, setIsMoveExistingOpen] = useState(false)
  const [availableProperties, setAvailableProperties] = useState<AvailableProperty[]>([])
  const [isLoadingProperties, setIsLoadingProperties] = useState(false)
  const [selectedAvailableId, setSelectedAvailableId] = useState<string | null>(null)
  const [isMovingExisting, setIsMovingExisting] = useState(false)

  const [isMoveConfirmOpen, setIsMoveConfirmOpen] = useState(false)
  const [propertyToMove, setPropertyToMove] = useState<{
    propertyId: string
    name: string
  } | null>(null)
  const [otherWorkspaces, setOtherWorkspaces] = useState<WorkspaceWithGrouping[]>([])
  const [isLoadingWorkspaces, setIsLoadingWorkspaces] = useState(false)
  const [targetWorkspaceId, setTargetWorkspaceId] = useState<string | null>(null)
  const [isMovingToWorkspace, setIsMovingToWorkspace] = useState(false)

  const [togglingPropertyId, setTogglingPropertyId] = useState<string | null>(null)

  const canManageSites = isOwner || isAdmin

  const workspaceProperties = useMemo(() => properties || [], [properties])
  const enabledCount = workspaceProperties.filter((p) => p.enabled).length

  const goToAddWebsite = () => {
    if (!workspace?.workspaceId || hasReachedWebsiteLimit) return
    navigate(`/add-website?workspace=${encodeURIComponent(workspace.workspaceId)}`)
  }

  useEffect(() => {
    if (!isMoveExistingOpen || availableProperties.length > 0 || !token) return
    setIsLoadingProperties(true)
    getAvailableProperties(token)
      .then((result) => {
        const filtered = (result.properties || []).filter(
          (p) => p.current_workspace_id !== workspace?.workspaceId,
        )
        setAvailableProperties(filtered)
      })
      .catch(() => toast.error('Failed to load available sites'))
      .finally(() => setIsLoadingProperties(false))
  }, [isMoveExistingOpen, availableProperties.length, token, workspace?.workspaceId])

  const handleMoveExistingClose = (open: boolean) => {
    if (!open) {
      setSelectedAvailableId(null)
      setAvailableProperties([])
    }
    setIsMoveExistingOpen(open)
  }

  const handleMoveExistingToWorkspace = async () => {
    if (!workspace || !token || !selectedAvailableId) return
    setIsMovingExisting(true)
    try {
      await movePropertyToWorkspace(workspace.workspaceId, selectedAvailableId, token)
      toast.success('Site moved to workspace')
      refetch()
      handleMoveExistingClose(false)
    } catch {
      toast.error('Failed to move site')
    } finally {
      setIsMovingExisting(false)
    }
  }

  const handleMoveClick = async (property: { propertyId: string; name: string }) => {
    setPropertyToMove(property)
    setTargetWorkspaceId(null)
    setIsMoveConfirmOpen(true)
    setIsLoadingWorkspaces(true)
    try {
      const all = await getAllWorkspaces(token!)
      setOtherWorkspaces(all.filter((w) => w.workspaceId !== workspace?.workspaceId))
    } catch {
      toast.error('Failed to load workspaces')
    } finally {
      setIsLoadingWorkspaces(false)
    }
  }

  const handleConfirmMove = async () => {
    if (!propertyToMove || !targetWorkspaceId || !token) return
    const targetName =
      otherWorkspaces.find((w) => w.workspaceId === targetWorkspaceId)?.name ?? 'workspace'
    setIsMovingToWorkspace(true)
    try {
      await movePropertyToWorkspace(targetWorkspaceId, propertyToMove.propertyId, token)
      toast.success(`"${propertyToMove.name}" moved to ${targetName}`)
      refetch()
      setIsMoveConfirmOpen(false)
      setPropertyToMove(null)
      setTargetWorkspaceId(null)
    } catch {
      toast.error('Failed to move site')
    } finally {
      setIsMovingToWorkspace(false)
    }
  }

  const handleToggleEnabled = async (propertyId: string, currentEnabled: boolean) => {
    setTogglingPropertyId(propertyId)
    try {
      await togglePropertyEnabled(propertyId, !currentEnabled)
      toast.success(currentEnabled ? 'Site disabled' : 'Site enabled')
    } catch {
      toast.error('Failed to update site')
    } finally {
      setTogglingPropertyId(null)
    }
  }

  if (isWorkspaceLoading) {
    return (
      <SettingsWell>
        <SettingsListCard title={<Skeleton className="h-4 w-28" />} description={<Skeleton className="mt-1 h-3 w-48" />}>
          <Skeleton className="h-[52px] w-full" />
          <Skeleton className="h-[52px] w-full" />
        </SettingsListCard>
      </SettingsWell>
    )
  }

  const rowAction =
    'h-7 rounded-md px-2 text-xs font-medium text-foreground/60 transition-colors hover:bg-muted hover:text-foreground'

  return (
    <TooltipProvider>
      <SettingsWell>
        <SettingsListCard
          title={
            <>
              Sites
              <span className="text-xs font-medium tabular-nums text-foreground/60">{workspaceProperties.length}</span>
            </>
          }
          description={`${enabledCount} enabled · ${members.length} team member${members.length !== 1 ? 's' : ''} have access`}
          action={
            canManageSites && (
              <div className="flex shrink-0 items-center gap-2">
                <Button variant="outline" onClick={() => setIsMoveExistingOpen(true)} className={settingsButtonClass}>
                  Move existing
                </Button>
                <CreateOrUpgradeButton
                  atLimit={hasReachedWebsiteLimit}
                  onClick={goToAddWebsite}
                  className={settingsButtonClass}
                  disabled={!workspace?.workspaceId}
                  noun="sites"
                  limit={websiteLimit}
                  planName={displayPlanName}
                >
                  Add site
                </CreateOrUpgradeButton>
              </div>
            )
          }
        >
          {hasReachedWebsiteLimit && (isReadOnly || websiteLimit > 0) && (
            <p className="text-xs leading-4 text-foreground/60">
              {isReadOnly ? (readOnlyReason === 'trial_ended' ? 'Trial ended. ' : 'Subscription ended. ') : `${websitesUsed} of ${websiteLimit} sites used. `}
              <button
                type="button"
                onClick={() => openPricingModal({ reason: 'website_limit' })}
                className="font-medium text-foreground underline underline-offset-2"
              >
                Upgrade
              </button>
            </p>
          )}
          {workspaceProperties.length === 0 ? (
            <p className="rounded-lg border border-dashed border-border px-4 py-6 text-center text-[13px] leading-5 text-foreground/60">
              {isReadOnly
                ? readOnlyReason === 'trial_ended'
                  ? 'Your trial has ended. Reactivate to add a site to this workspace.'
                  : 'Your subscription has ended. Upgrade to add a site to this workspace.'
                : hasReachedWebsiteLimit && websiteLimit > 0
                  ? siteLimitCopy.description
                  : 'No sites yet. Add a site to start tracking analytics in this workspace.'}
            </p>
          ) : (
            <SettingsList>
              {workspaceProperties.map((property) => (
                <SettingsListRow
                  key={property.propertyId}
                  leading={
                    <span className={cn('size-5 shrink-0 overflow-hidden rounded bg-card ring-1 ring-border', !property.enabled && 'opacity-50')}>
                      <Favicon domain={property.domain} size={20} className="rounded-sm" />
                    </span>
                  }
                  primary={<span className={cn(!property.enabled && 'text-foreground/50')}>{property.name}</span>}
                  secondary={property.domain}
                  trailing={
                    <>
                      <TrackingPill status={property.trackingStatus} />
                      <button
                        type="button"
                        className={rowAction}
                        onClick={() =>
                          navigate(`/p/${generateSlugId(property.name || property.domain, property.propertyId)}/settings`)
                        }
                      >
                        Settings
                      </button>
                      {canManageSites && (
                        <>
                          <button type="button" className={rowAction} onClick={() => handleMoveClick(property)}>
                            Move
                          </button>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="flex items-center pl-1">
                                <Switch
                                  aria-label={`${property.enabled ? 'Disable' : 'Enable'} ${property.domain} in the workspace dashboard`}
                                  checked={property.enabled}
                                  onCheckedChange={() => handleToggleEnabled(property.propertyId, property.enabled)}
                                  disabled={togglingPropertyId === property.propertyId}
                                />
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>{property.enabled ? 'Disable' : 'Enable'} in workspace dashboard</TooltipContent>
                          </Tooltip>
                        </>
                      )}
                    </>
                  }
                />
              ))}
            </SettingsList>
          )}
        </SettingsListCard>
      </SettingsWell>

      <Dialog open={isMoveExistingOpen} onOpenChange={handleMoveExistingClose}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle className="text-base font-medium">Move existing site</DialogTitle>
            <DialogDescription className="text-[13px]">
              Move a site from another workspace into this one.
            </DialogDescription>
          </DialogHeader>

          {isLoadingProperties ? (
            <div className="flex items-center justify-center py-10 text-muted-foreground gap-2">
              <HugeiconsIcon icon={Loading01Icon} size={16} className="animate-spin" />
              <span className="text-sm">Loading sites…</span>
            </div>
          ) : availableProperties.length === 0 ? (
            <EmptyState
              icon={<HugeiconsIcon icon={EarthIcon} size={28} />}
              title="No other sites available"
              description="All your sites are already in this workspace, or you have none to move."
              className="py-8"
            />
          ) : (
            <div className="space-y-1 max-h-60 overflow-y-auto pr-1">
              {availableProperties.map((prop) => (
                <button
                  key={prop.property_id}
                  type="button"
                  onClick={() =>
                    setSelectedAvailableId(
                      selectedAvailableId === prop.property_id ? null : prop.property_id,
                    )
                  }
                  className={cn(
                    'w-full text-left flex items-center gap-2.5 px-3 py-2.5 rounded-lg border transition-colors',
                    selectedAvailableId === prop.property_id
                      ? 'border-border/60 bg-muted/40'
                      : 'border-border/30 hover:bg-muted/30',
                  )}
                >
                  <div className="shrink-0 size-5 rounded overflow-hidden ring-1 ring-border/40 bg-card">
                    <Favicon domain={prop.domain} size={20} className="rounded-sm" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-[13px] font-medium truncate">{prop.name}</p>
                    <p className="text-[11px] text-muted-foreground truncate">
                      {prop.domain}
                    </p>
                  </div>
                  {prop.current_workspace_name && (
                    <span className="text-[11px] text-muted-foreground border border-border/30 rounded px-1.5 py-0.5 shrink-0">
                      {prop.current_workspace_name}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}

          {selectedAvailableId && (
            <Alert className="mt-3 border-warning/20 bg-warning/[0.06]">
              <AlertDescription className="text-[11px] text-warning">
                This site will be removed from its current workspace.
              </AlertDescription>
            </Alert>
          )}

          <DialogFooter className="mt-4">
            <Button
              variant="outline"
              onClick={() => handleMoveExistingClose(false)}
              disabled={isMovingExisting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleMoveExistingToWorkspace}
              disabled={isMovingExisting || !selectedAvailableId}
              className="gap-1.5"
            >
              {isMovingExisting ? (
                <>
                  <HugeiconsIcon icon={Loading01Icon} size={14} className="animate-spin" />
                  Moving…
                </>
              ) : (
                'Move to workspace'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={isMoveConfirmOpen}
        onOpenChange={(open) => {
          if (!isMovingToWorkspace) {
            setIsMoveConfirmOpen(open)
            if (!open) {
              setPropertyToMove(null)
              setTargetWorkspaceId(null)
            }
          }
        }}
      >
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle className="text-base font-medium">
              Move site to another workspace
            </DialogTitle>
            <DialogDescription className="text-[13px]">
              Moving{' '}
              <span className="font-medium text-foreground">&quot;{propertyToMove?.name}&quot;</span>{' '}
              removes it from this workspace and grants the target workspace&apos;s team access.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2 py-1">
            {isLoadingWorkspaces ? (
              <div className="flex items-center gap-2 py-6 justify-center text-muted-foreground">
                <HugeiconsIcon icon={Loading01Icon} size={16} className="animate-spin" />
                <span className="text-sm">Loading workspaces…</span>
              </div>
            ) : otherWorkspaces.length === 0 ? (
              <EmptyState
                icon={<HugeiconsIcon icon={EarthIcon} size={28} />}
                title="No other workspaces"
                description="Create another workspace first to move this site."
                className="py-6"
              />
            ) : (
              <div className="space-y-1 max-h-52 overflow-y-auto pr-1">
                {otherWorkspaces.map((ws) => (
                  <button
                    key={ws.workspaceId}
                    type="button"
                    onClick={() => setTargetWorkspaceId(ws.workspaceId)}
                    className={cn(
                      'w-full text-left flex items-center gap-2.5 px-3 py-2.5 rounded-lg border transition-colors',
                      targetWorkspaceId === ws.workspaceId
                        ? 'border-border/60 bg-muted/40'
                        : 'border-border/30 hover:bg-muted/30',
                    )}
                  >
                    <HugeiconsIcon
                      icon={EarthIcon}
                      size={16}
                      className="text-muted-foreground shrink-0"
                    />
                    <div className="min-w-0 flex-1">
                      <p className="text-[13px] font-medium truncate">{ws.name}</p>
                      <p className="text-[11px] text-muted-foreground">
                        {ws.properties?.length ?? 0} site
                        {ws.properties?.length !== 1 ? 's' : ''}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsMoveConfirmOpen(false)}
              disabled={isMovingToWorkspace}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmMove}
              disabled={isMovingToWorkspace || !targetWorkspaceId}
              className="gap-1.5"
            >
              {isMovingToWorkspace ? (
                <>
                  <HugeiconsIcon icon={Loading01Icon} size={16} className="animate-spin" />
                  Moving…
                </>
              ) : (
                'Move site'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </TooltipProvider>
  )
}
